import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { JSXElementConstructor } from "react";

/**
 * Button Component
 *
 * A flexible, theme-aware button supporting multiple variants, hierarchies, and sizes.
 * Also supports icon-only states and can render as a Radix `Slot` for polymorphic behavior.
 */

// Define variant styling with class-variance-authority
const buttonVariants = cva(
  `inline-flex items-center justify-center gap-2 whitespace-nowrap disabled:pointer-events-none outline-none cursor-pointer transition-colors `,
  {
    variants: {
      variant: {
        primary: "text-gray-base",
        secondary: "text-white",
        error: "text-white",
        neutral: "text-gray-900",
      },
      hierarchy: {
        filled: "",
        tonal: "",
        outline: "border",
        ghost: "bg-transparent",
      },
      state: {
        default: "",
        hover: "",
        active: "",
        focus: "",
        disabled:
          "text-[var(--text-gray-lighter)] bg-[var(--fill-base-white)] cursor-not-allowed",
      },
      size: {
        xs: "text-xs h-6 px-3 py-1 rounded-sm",
        sm: "text-sm h-7 px-3 py-1 rounded-md",
        md: "text-sm h-8 px-4 py-1.5 rounded-md",
        lg: "text-sm h-9 px-4 py-2 rounded-md",
        xl: "text-md h-12 px-5 py-3 rounded-md",
      },
    },

    // Define combined behavior for each color-hierarchy pair
    compoundVariants: [
      // Primary
      {
        variant: "primary",
        hierarchy: "filled",
        className:
          "bg-[var(--fill-base-main)] text-white hover:bg-[var(--fill-primary-solid-strongest)] active:bg-[var(--fill-primary-solid-stronger)] disabled:bg-[var(--fill-gray-lighter)]",
      },
      {
        variant: "primary",
        hierarchy: "tonal",
        className:
          "bg-[var(--fill-primary-tonal-lighter)] text-[var(--text-base-primary)] hover:bg-[var(--fill-primary-tonal-normal)] active:bg-[var(--fill-primary-tonal-stronger)] disabled:bg-[var(--fill-base-gray)]",
      },
      {
        variant: "primary",
        hierarchy: "outline",
        className:
          "bg-[var(--fill-base-white)] border-[var(--stroke-base-primary)] text-[var(--text-base-primary)] hover:bg-[var(--fill-primary-tonal-lighter)] active:bg-[var(--fill-primary-tonal-normal)] disabled:border-[var(--stroke-gray-light)]",
      },
      {
        variant: "primary",
        hierarchy: "ghost",
        className:
          "text-[var(--text-base-primary)] hover:bg-[var(--fill-primary-tonal-lighter)] active:bg-[var(--fill-primary-tonal-normal)]",
      },
      // Secondary
      {
        variant: "secondary",
        hierarchy: "filled",
        className:
          "bg-[var(--fill-secondary-solid-normal)] text-[var(--text-base-primary)] hover:bg-[var(--fill-secondary-solid-lighter)] active:bg-[var(--fill-secondary-solid-stronger)] disabled:bg-[var(--fill-gray-lighter)]",
      },
      {
        variant: "secondary",
        hierarchy: "tonal",
        className:
          "bg-[var(--fill-secondary-tonal-lighter)] text-[var(--text-base-secondary)] hover:bg-[var(--fill-secondary-tonal-normal)] active:bg-[var(--fill-secondary-tonal-stronger)] disabled:bg-[var(--fill-base-gray)]",
      },
      {
        variant: "secondary",
        hierarchy: "outline",
        className:
          "bg-[var(--fill-base-white)] border-[var(--stroke-base-secondary)] text-[var(--text-base-secondary)] hover:bg-[var(--fill-secondary-tonal-lighter)] active:bg-[var(--fill-secondary-tonal-normal) disabled:border-[var(--stroke-gray-light)]",
      },
      {
        variant: "secondary",
        hierarchy: "ghost",
        className:
          "text-[var(--text-base-secondary)] hover:bg-[var(--fill-secondary-tonal-lighter)] active:bg-[var(--fill-secondary-tonal-normal)]",
      },
      // error
      {
        variant: "error",
        hierarchy: "filled",
        className:
          "bg-[var(--fill-red-solid-normal)] text-white hover:bg-[var(--fill-red-solid-lighter)] active:bg-[var(--fill-red-solid-stronger)] disabled:bg-[var(--fill-gray-lighter)]",
      },
      {
        variant: "error",
        hierarchy: "tonal",
        className:
          "bg-[var(--fill-red-tonal-lighter)] text-[var(--text-base-red)] hover:bg-[var(--fill-red-tonal-normal)] active:bg-[var(--fill-red-tonal-stronger)] disabled:bg-[var(--fill-base-gray)]",
      },
      {
        variant: "error",
        hierarchy: "outline",
        className:
          "bg-[var(--fill-base-white)] border-[var(--stroke-base-red)] text-[var(--text-base-red)] hover:bg-[var(--fill-red-tonal-lighter)] active:bg-[var(--fill-red-tonal-normal)] disabled:border-[var(--stroke-gray-light)]",
      },
      {
        variant: "error",
        hierarchy: "ghost",
        className:
          "text-[var(--text-base-red)] hover:bg-[var(--fill-red-tonal-lighter)] active:bg-[var(--fill-red-tonal-normal)]",
      },
      // Neutral
      {
        variant: "neutral",
        hierarchy: "filled",
        className:
          "bg-[var(--fill-gray-stronger)] text-white hover:bg-[var(--fill-gray-normal)] active:bg-[var(--fill-gray-strongest)] disabled:bg-[var(--fill-gray-lighter)]",
      },
      {
        variant: "neutral",
        hierarchy: "tonal",
        className:
          "bg-[var(--fill-base-gray)] text-[var(--text-gray-normal)] hover:bg-[var(--fill-gray-lighter)] active:bg-[var(--fill-gray-light)] disabled:bg-[var(--fill-base-gray)]",
      },
      {
        variant: "neutral",
        hierarchy: "outline",
        className:
          "bg-[var(--fill-base-white)] border-[var(--stroke-gray-normal)] text-[var(--text-gray-normal)] hover:bg-[var(--fill-base-gray)] active:bg-[var(--fill-gray-lighter)] disabled:border-[var(--stroke-gray-light)]",
      },
      {
        variant: "neutral",
        hierarchy: "ghost",
        className:
          "text-[var(--text-gray-normal)] hover:bg-[var(--fill-base-gray)] active:bg-[var(--fill-gray-lighter)]",
      },
    ],
    defaultVariants: {
      variant: "primary",
      hierarchy: "filled",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

// Padding and sizing adjustments for icon-only buttons
const iconPadding = {
  xs: "px-1 w-6 py-1",
  sm: "px-1 w-7 py-1",
  md: "px-1 w-8 5 py-1.5",
  lg: "px-2 w-9 py-2",
  xl: "px-3 w-12 py-3",
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, hierarchy, size, asChild, loading = false, ...props }, ref) => {
    // Allows polymorphic rendering via Radix Slot
    const Comp = asChild ? Slot : "button";

    // Detect icon-only button (used to apply compact padding)
    const childArray = React.Children.toArray(props.children);
    const isIconOnly =
      childArray?.length == 1 &&
      React.isValidElement(childArray[0]) &&
      (childArray[0]?.type as JSXElementConstructor<object>)?.name === "Icon";

    // When asChild is true, Slot requires exactly one React element child
    // So we pass children directly without any wrapper or additional elements
    if (asChild) {
      return (
        <Comp
          className={cn(
            buttonVariants({ variant, hierarchy, size, className }),
            isIconOnly &&
            `${iconPadding[size as "xs" | "sm" | "md" | "lg" | "xl"]}`,
          )}
          ref={ref}
          {...props}
        />
      );
    }

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, hierarchy, size, className }),
          isIconOnly &&
          `${iconPadding[size as "xs" | "sm" | "md" | "lg" | "xl"]}`,
        )}
        ref={ref}
        {...props}
      >
        {loading && (
          <span className="mr-2 h-4 w-4 border-2 border-black border-t-transparent rounded-full animate-spin"></span>
        )}
        {props.children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants, iconPadding };
